/**
 * Zod schemas dos dashboards nativos (tabela `public.dashboards` +
 * `public.dashboard_widgets`). Enum de `widget_type` alinhado com a
 * check constraint da migration 0016.
 */
import { z } from 'zod';

export const dashboardScopeSchema = z.enum(['global', 'clinic', 'personal']);
export type DashboardScope = z.infer<typeof dashboardScopeSchema>;

export const widgetTypeSchema = z.enum([
  'kpi',
  'line',
  'bar',
  'pie',
  'area',
  'table',
  'narrative',
  'ai_card',
]);
export type WidgetType = z.infer<typeof widgetTypeSchema>;

export const widgetPositionSchema = z
  .object({
    x: z.number().int().min(0).max(11).default(0),
    y: z.number().int().min(0).max(99).default(0),
    w: z.number().int().min(1).max(12).default(4),
    h: z.number().int().min(1).max(12).default(2),
  })
  .default({ x: 0, y: 0, w: 4, h: 2 });

export const dashboardSchema = z.object({
  id: z.string().uuid(),
  owner_id: z.string().uuid(),
  scope: dashboardScopeSchema,
  clinic_id: z.string().uuid().nullable(),
  name: z.string().min(1),
  description: z.string().nullable(),
  layout: z.record(z.string(), z.unknown()).default({}),
  is_shared: z.boolean().default(false),
  is_default: z.boolean().default(false),
  icon: z.string().nullable(),
  sort_order: z.number().int().default(100),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Dashboard = z.infer<typeof dashboardSchema>;

export const dashboardWidgetSchema = z.object({
  id: z.string().uuid(),
  dashboard_id: z.string().uuid(),
  widget_type: widgetTypeSchema,
  title: z.string().min(1),
  subtitle: z.string().nullable(),
  query_ref: z.string().min(1),
  config: z.record(z.string(), z.unknown()).default({}),
  position: widgetPositionSchema,
  refresh_interval_sec: z.number().int().min(30).nullable(),
  is_visible: z.boolean().default(true),
  created_at: z.string(),
  updated_at: z.string(),
});
export type DashboardWidget = z.infer<typeof dashboardWidgetSchema>;

/** Input para criar dashboard. */
export const createDashboardInputSchema = z.object({
  name: z.string().trim().min(1, 'Nome obrigatório').max(120),
  description: z.string().max(500).nullable().default(null),
  scope: dashboardScopeSchema.default('global'),
  clinic_id: z.string().uuid().nullable().default(null),
  is_shared: z.boolean().default(false),
});
export type CreateDashboardInput = z.infer<typeof createDashboardInputSchema>;

/** Input para criar widget. `query_ref` livre (string) — M1 trará um
 * seletor com views disponíveis. */
export const createWidgetInputSchema = z.object({
  dashboard_id: z.string().uuid(),
  widget_type: widgetTypeSchema,
  title: z.string().trim().min(1).max(120),
  subtitle: z.string().max(200).nullable().default(null),
  query_ref: z.string().trim().min(1).max(120),
  config: z.record(z.string(), z.unknown()).default({}),
  position: widgetPositionSchema,
});
export type CreateWidgetInput = z.infer<typeof createWidgetInputSchema>;
